import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";

import {
  buildInitialFormFromAlbum,
  normalizeAlbumPayload,
  validateAlbumForm,
} from "../utils/albumsPhotosUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

export default function AlbumForm({
  mode = "create",
  album,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => buildInitialFormFromAlbum(album), [album]);

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeAlbumPayload(formData);
    const errorMessage = validateAlbumForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Informations de l’album
        </h3>

        <div className="mt-4 grid gap-4">
          <div>
            <label className={labelClass}>
              Titre de l’album <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              placeholder="Ex : Stage de printemps, Tournoi du club..."
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              placeholder="Ajoutez une courte description de l’album."
              className={inputClass}
            />
          </div>

          <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
            <div>
              <p className="font-bold text-slate-900 dark:text-white">
                Album actif
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Un album actif peut être affiché côté visiteur.
              </p>
            </div>

            <input
              type="checkbox"
              name="is_active"
              checked={Boolean(formData.is_active)}
              onChange={handleChange}
              className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
            />
          </label>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
              ? "Créer l’album"
              : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}